import React, { Fragment, useEffect, useRef, useState } from 'react';
import Logo from '../../assets/logo-sm.svg';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { Redirect, useLocation } from 'react-router';
import { login, reset } from '../../slices/userSlice';
import { Link } from 'react-router-dom';
import SubmitButton from '../../components/submitButton';
import { Notification } from '../../components/notifications';
import { Role } from '../../common/constants';
import { getPublicConfig } from '../../api/services/configService';

type LoginState = {
  referrer?: string;
};

const Login = () => {
  const dispatch = useAppDispatch();
  const { state } = useLocation<LoginState>();

  // Refs
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // State
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>('');

  useEffect(() => {
    let interval = setTimeout(() => {
      if (emailRef?.current && passwordRef?.current) {
        let pass = passwordRef?.current?.value ?? '';
        let email = emailRef?.current?.value ?? '';

        setPassword(pass);
        setEmailAddress(email);

        clearInterval(interval);
      }
    }, 100);
  });

  // Selectors
  const validateCaptcha =
    loginAttempts >= Number(process.env.REACT_APP_CAPTCHA_LOGIN_ATTEMPTS ?? 5);

  const hasError = useAppSelector((s) => s.user.status === 'failed');
  const errorMessage = useAppSelector(s => s.user.status === 'failed' ? (s.user.statusMessage ?? 'The credentials provided are invalid') : '');
  const pendingLogin = useAppSelector((s) => s.user.status === 'logging_in');
  const isLoggedIn = useAppSelector((s) => s.user.status === 'authenticated');
  const userRoles = useAppSelector((s) => s.user.roles);

  // MFA selectors
  const isMfaRequired = useAppSelector((s) => s.user.status === 'mfa_required');
  const mfaToken = useAppSelector((s) => s.user.mfaToken);
  const mfaSetupRequired = useAppSelector((s) => s.user.mfaSetupRequired);
  const qrCodeUri = useAppSelector((s) => s.user.qrCodeUri);
  const manualKey = useAppSelector((s) => s.user.manualKey);

  const isPasswordAboutToExpire = useAppSelector(
    (s) => s.user.passwordAboutToExpire
  );

  // Redirect to MFA verification page
  if (isMfaRequired && mfaToken) {
    return (
      <Redirect
        to={{
          pathname: '/mfa',
          state: {
            mfaToken,
            mfaSetupRequired: mfaSetupRequired ?? false,
            qrCodeUri,
            manualKey,
          },
        }}
      />
    );
  }

  if (isLoggedIn) {
    if (isPasswordAboutToExpire)
      return <Redirect to='/forced-change-password' />;

    if (state?.referrer !== undefined)
      return <Redirect to="/" />

    if (userRoles?.includes(Role.Admin))
      return <Redirect to="/admin/therapists" />

    if (userRoles?.includes(Role.BillingSpecialist))
      return <Redirect to="/proofing" />

    if (userRoles?.includes(Role.Therapist))
      return <Redirect to="/" />

    return <Redirect to='/' />;
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginAttempts(loginAttempts + 1);
    dispatch(login({ emailAddress, password, captchaToken }));
  };

  const closeErrors = () => {
    dispatch(reset());
  };

  const isTesting = process.env.REACT_APP_IS_TESTING ?? false;

  return (
    <div className='flex bg-ptsblue-normal flex-col w-full items-center justify-center' style={{ height: isTesting ? '95vh' : '100vh' }}>
      <div className='text-gray-900 w-full p-6 md:max-w-xl'>
        <form
          className='bg-white rounded-lg border shadow-lg p-10 mb-2'
          onSubmit={handleSubmit}
        >
          <div className='flex justify-center mb-6'>
            <img
              src={Logo}
              alt='Pediatric Therapeutic Services'
              className='object-contain object-center h-28 w-auto max-w-full'
            />
          </div>

          {hasError && (
            <Notification
              isSuccess={false}
              isOpen={hasError}
              onClose={closeErrors}
            >
              <Fragment>
                <span style={{ whiteSpace: 'pre-line' }}>{errorMessage}</span>
                              {errorMessage.includes('disabled') && <p>Please reach out to <a> Support-kellypediatrictherapy@kellyservices.com</a> if you feel this was done in error.</p>}
                {validateCaptcha && (
                  <React.Fragment>
                    &nbsp;
                    <Link
                      className='block text-sm text-white underline'
                      to='/forgot-password'
                    >
                      If you forgot your password, please click here
                    </Link>
                  </React.Fragment>
                )}
              </Fragment>
            </Notification>
          )}

          <div className='mb-4'>
            <label className='block mb-2' htmlFor='emailaddress'>
              Email Address
            </label>
            <input
              id='emailaddress'
              onChange={(event) => setEmailAddress(event.target.value)}
              value={emailAddress}
              ref={emailRef}
              type='text'
              placeholder='Email Address'
            />
          </div>

          <div className='mb-6'>
            <label className='block mb-2' htmlFor='password'>
              Password
            </label>
            <input
              id='password'
              value={password}
              ref={passwordRef}
              onChange={(event) => setPassword(event.target.value)}
              type='password'
              placeholder='***************************'
            />
          </div>

          {validateCaptcha && (
            <div className='mb-6 flex justify-center'>
              <ReCAPTCHA
                sitekey={getPublicConfig().captchaSiteKey}
                onChange={(token) => setCaptchaToken(token)}
              />
            </div>
          )}

          <div className='flex items-center justify-between'>
            <SubmitButton spinning={pendingLogin} text='Log In' />
            <Link to='/forgot-password'>Forgot password?</Link>
          </div>
        </form>
        <p className='text-right text-gray-300 text-xs'>
          &copy;Copyright 2026 – Kelly Pediatric Therapy.
          <br />
          All rights reserved.
          <span className='mt-1 block text-ptsblue-dark'>{`~version-${process.env.REACT_APP_VCS_NUMBER?.slice(0, 11)}`}</span>
        </p>
      </div>
    </div>
  );
};

export default Login;
